/**
 * Access Control Page
 *
 * Unified RBAC interface with 3 tabs:
 * 1. Users - List all users, their org, roles, group memberships
 * 2. Groups - Create/manage groups, assign users
 * 3. Roles & Permissions - Visual matrix of role → permission mappings
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import {
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import api, { tenantAPI, Tenant } from '../services/api';
import UserManagement from './UserManagement';
import GroupManagement from './GroupManagement';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface RolePermission {
  id: string;
  role: string;
  permission_name: string;
  description: string;
}

// Permission categories for organized display
const PERMISSION_CATEGORIES = [
  {
    name: 'User Management',
    permissions: ['manage_users', 'manage_organizations'],
  },
  {
    name: 'Project & Data',
    permissions: ['manage_projects', 'manage_datasets', 'view_datasets'],
  },
  {
    name: 'Analytics & Export',
    permissions: ['view_analytics', 'export_data'],
  },
  {
    name: 'Features',
    permissions: ['edit_maps', 'use_ai_features'],
  },
  {
    name: 'Administration',
    permissions: ['manage_deployments', 'view_audit_logs'],
  },
];

// All roles in order of privilege
const ROLES = ['admin', 'editor', 'analyst', 'viewer'];
const ROLE_COLORS: Record<string, 'error' | 'warning' | 'info' | 'success'> = {
  admin: 'error',
  editor: 'warning',
  analyst: 'info',
  viewer: 'success',
};

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`access-control-tabpanel-${index}`}
      aria-labelledby={`access-control-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `access-control-tab-${index}`,
    'aria-controls': `access-control-tabpanel-${index}`,
  };
}

const AccessControlPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Role permissions state
  const [rolePermissions, setRolePermissions] = useState<RolePermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);

  // Load tenants on mount
  useEffect(() => {
    loadTenants();
  }, []);

  // Load role permissions when tab changes to Permissions
  useEffect(() => {
    if (activeTab === 2) {
      loadRolePermissions();
    }
  }, [activeTab]);

  const loadTenants = async () => {
    try {
      setTenantsLoading(true);
      const data = await tenantAPI.list();
      setTenants(data);
    } catch (err) {
      console.error('Error loading tenants:', err);
      setError('Failed to load organizations');
    } finally {
      setTenantsLoading(false);
    }
  };

  const loadRolePermissions = async () => {
    try {
      setPermissionsLoading(true);
      setError(null);

      const response = await api.get('/permissions/roles');
      setRolePermissions(response.data.permissions || []);
    } catch (err) {
      console.error('Error loading role permissions:', err);
      setError('Failed to load role permissions');
    } finally {
      setPermissionsLoading(false);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleTenantChange = (event: SelectChangeEvent<string>) => {
    setSelectedTenantId(event.target.value);
  };

  // Check if a role has a specific permission
  const hasPermission = (role: string, permission: string): boolean => {
    return rolePermissions.some(
      (rp) => rp.role === role && rp.permission_name === permission
    );
  };

  // Render the permissions matrix
  const renderPermissionsMatrix = () => {
    if (permissionsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">Role Permissions Matrix</Typography>
          <Tooltip title="Refresh permissions">
            <IconButton onClick={loadRolePermissions} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This matrix shows which permissions each role has. Permissions are
            currently read-only. Contact a super admin to modify role
            permissions.
          </Typography>
        </Alert>

        <TableContainer component={Paper} sx={{ mb: 4 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold', width: 250 }}>
                  Permission
                </TableCell>
                {ROLES.map((role) => (
                  <TableCell key={role} align="center" sx={{ width: 120 }}>
                    <Chip
                      label={role.charAt(0).toUpperCase() + role.slice(1)}
                      color={ROLE_COLORS[role]}
                      size="small"
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {PERMISSION_CATEGORIES.map((category) => (
                <React.Fragment key={category.name}>
                  <TableRow>
                    <TableCell
                      colSpan={ROLES.length + 1}
                      sx={{
                        bgcolor: 'grey.50',
                        fontWeight: 'bold',
                        color: 'text.secondary',
                      }}
                    >
                      {category.name}
                    </TableCell>
                  </TableRow>
                  {category.permissions.map((permission) => (
                    <TableRow key={permission} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {permission
                              .split('_')
                              .map(
                                (word) =>
                                  word.charAt(0).toUpperCase() + word.slice(1)
                              )
                              .join(' ')}
                          </Typography>
                          <Tooltip
                            title={
                              rolePermissions.find(
                                (rp) => rp.permission_name === permission
                              )?.description || permission
                            }
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ color: 'grey.400' }}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                      {ROLES.map((role) => (
                        <TableCell key={role} align="center">
                          {hasPermission(role, permission) ? (
                            <CheckIcon color="success" />
                          ) : (
                            <CancelIcon sx={{ color: 'grey.300' }} />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Role Descriptions */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Role Descriptions
        </Typography>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Chip label="Admin" color="error" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">
                Full access to all features. Can manage users, projects, datasets,
                deployments, and view audit logs.
              </Typography>
            </Box>
            <Box>
              <Chip label="Editor" color="warning" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">
                Can manage datasets and maps. Cannot manage users or deployments.
              </Typography>
            </Box>
            <Box>
              <Chip label="Analyst" color="info" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">
                Can view and analyze data, export reports, and use AI features.
                Read-only access to datasets.
              </Typography>
            </Box>
            <Box>
              <Chip label="Viewer" color="success" size="small" sx={{ mr: 1 }} />
              <Typography variant="body2" component="span">
                Read-only access. Can view datasets, analytics, and use AI features.
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Action Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Manage users, groups, and role-based access control
        </Typography>

        {/* Organization Filter - shown for Users and Groups tabs */}
        {activeTab !== 2 && (
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Filter by Organization</InputLabel>
            <Select
              value={selectedTenantId}
              onChange={handleTenantChange}
              label="Filter by Organization"
              disabled={tenantsLoading}
            >
              <MenuItem value="">
                <em>{tenantsLoading ? 'Loading...' : 'All Organizations'}</em>
              </MenuItem>
              {tenants.map((tenant) => (
                <MenuItem key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.organization_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="access control tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            icon={<PeopleIcon />}
            iconPosition="start"
            label="Organization Members"
            {...a11yProps(0)}
          />
          <Tab
            icon={<GroupIcon />}
            iconPosition="start"
            label="Permission Groups"
            {...a11yProps(1)}
          />
          <Tab
            icon={<SecurityIcon />}
            iconPosition="start"
            label="Roles & Permissions"
            {...a11yProps(2)}
          />
        </Tabs>

        {/* Tab Panels */}
        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Tooltip title="Users who belong to organizations and can access GUS projects based on their role.">
                <InfoIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              </Tooltip>
              Organization members are end users within a tenant. Each user has a base role
              (admin/editor/analyst/viewer) that determines their default permissions.
            </Typography>
            <UserManagement />
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <Tooltip title="Groups allow you to assign the same permissions to multiple users at once.">
                <InfoIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
              </Tooltip>
              Permission groups let you batch-assign permissions to multiple users.
              Users in a group inherit all of the group's permissions.
            </Typography>
            <GroupManagement tenantId={selectedTenantId || undefined} />
          </TabPanel>

          <TabPanel value={activeTab} index={2}>
            {renderPermissionsMatrix()}
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default AccessControlPage;
